import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';
import { CONTACT_INFO, SOCIAL_LINKS } from '../config/constants';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#1e3a8a] text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Company Info & Brokerage */}
          <div className="text-center md:text-left">
            <img
              src="/team-shailesh-white-logo.png"
              alt="Team Shailesh"
              className="h-20 w-auto object-contain mb-4 mx-auto md:mx-0"
            />
            <p className="text-blue-100 mb-4">
              Your trusted partner in buying, selling, and growing commercial businesses across Canada.
            </p>
            {/* Social Media Icons */}
            <div className="flex justify-center md:justify-start space-x-3 mb-4">
              <a href={SOCIAL_LINKS.FACEBOOK} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full flex items-center justify-center text-blue-200 hover:text-[#E31837] hover:bg-blue-800/50 transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href={SOCIAL_LINKS.INSTAGRAM} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full flex items-center justify-center text-blue-200 hover:text-[#E31837] hover:bg-blue-800/50 transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href={SOCIAL_LINKS.YOUTUBE} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full flex items-center justify-center text-blue-200 hover:text-[#E31837] hover:bg-blue-800/50 transition-colors" aria-label="YouTube">
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
            </div>
            {/* Brokerage Logo */}
            <div className="mt-4 pt-4 border-t border-blue-700 flex justify-center md:justify-start">
              <img
                src="/remax-ultimate-white.png"
                alt="RE/MAX Ultimate Realty Inc., Brokerage"
                className="h-20 w-auto object-contain"
                width="159"
                height="80"
              />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#E31837]">Quick Links</h3>
            <ul className="space-y-0">
              <li><Link to="/" className="text-blue-100 hover:text-white transition-colors inline-block py-2.5">Home</Link></li>
              <li><Link to="/about" className="text-blue-100 hover:text-white transition-colors inline-block py-2.5">About Us</Link></li>
              <li><Link to="/listings" className="text-blue-100 hover:text-white transition-colors inline-block py-2.5">Exclusive Listings</Link></li>
              <li><Link to="/contact" className="text-blue-100 hover:text-white transition-colors inline-block py-2.5">Contact</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#E31837]">Services</h3>
            <ul className="space-y-0">
              <li><Link to="/services/buy" className="text-blue-100 hover:text-white transition-colors inline-block py-2.5">Buy a Business</Link></li>
              <li><Link to="/services/grow" className="text-blue-100 hover:text-white transition-colors inline-block py-2.5">Grow Your Business</Link></li>
              <li><Link to="/services/sell" className="text-blue-100 hover:text-white transition-colors inline-block py-2.5">Sell a Business</Link></li>
              <li><Link to="/about/team" className="text-blue-100 hover:text-white transition-colors inline-block py-2.5">Our Team</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#E31837]">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Phone className="w-5 h-5 mr-2 mt-1 flex-shrink-0" />
                <div>
                  <a href={`tel:${CONTACT_INFO.PRIMARY_PHONE_RAW}`} className="text-blue-100 hover:text-white transition-colors block py-2.5">
                    {CONTACT_INFO.PRIMARY_PHONE}
                  </a>
                  <a href={`tel:${CONTACT_INFO.OFFICE_PHONE_RAW}`} className="text-blue-100 hover:text-white transition-colors block text-sm py-2.5">
                    {CONTACT_INFO.OFFICE_PHONE} (Office)
                  </a>
                </div>
              </li>
              <li className="flex items-start">
                <Mail className="w-5 h-5 mr-2 mt-1 flex-shrink-0" />
                <a href={`mailto:${CONTACT_INFO.EMAIL}`} className="text-blue-100 hover:text-white transition-colors py-2.5 inline-block">
                  {CONTACT_INFO.EMAIL}
                </a>
              </li>
              <li className="flex items-start">
                <MapPin className="w-5 h-5 mr-2 mt-1 flex-shrink-0" />
                <span className="text-blue-100">
                  {CONTACT_INFO.ADDRESS}
                </span>
              </li>
            </ul>
            {/* RE/MAX Balloon */}
            <div className="mt-4 pt-4 border-t border-blue-700 flex items-center">
              <img 
                src="/remax-balloon.png" 
                alt="RE/MAX" 
                className="h-12 w-auto object-contain"
                width="43"
                height="48"
              />
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-blue-800 mt-8 pt-8 text-center text-blue-100">
          <p className="text-sm">
            © {currentYear} Team Shailesh | RE/MAX Ultimate Realty Inc., Brokerage. All rights reserved.
          </p>
          <p className="text-xs mt-2">
            Independently Owned and Operated
          </p>
          <div className="flex justify-center space-x-4 mt-3 text-xs">
            <Link to="/privacy-policy" className="hover:text-white transition-colors inline-block py-2">Privacy Policy</Link>
            <span className="py-2">|</span>
            <Link to="/terms-of-use" className="hover:text-white transition-colors inline-block py-2">Terms of Use</Link>
            <span className="py-2">|</span>
            <a href="https://www.reco.on.ca" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors inline-block py-2">RECO</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;